import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance } from 'axios';
import { getAuth } from './auth';
import { setBearerAuthorization } from './bearerAuthorization';
import { notifySessionExpired } from './sessionExpiry';

/**
 * Creates an axios instance with standard interceptors
 */
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor to add auth token to all requests
  instance.interceptors.request.use(
    async (config) => {
      try {
        const auth = getAuth();
        const token = await auth.getToken();

        setBearerAuthorization(config.headers, token);
        // Do not send X-Tenant-ID: strict tenant identity is derived from validated JWT claims.
      } catch (error) {
        console.warn('Auth context not available, skipping token attachment');
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor to handle token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        try {
          await getAuth().refresh();
          const newToken = await getAuth().getToken();

          // Create a new config object with updated headers to avoid mutating readonly properties
          const retryConfig: AxiosRequestConfig & { _retry: boolean } = {
            ...originalRequest,
            _retry: true,
            headers: {
              ...originalRequest.headers,
              ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
            },
          };

          return instance(retryConfig);
        } catch (refreshError) {
          // If token refresh fails, prompt re-authentication instead of a silent logout.
          notifySessionExpired();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Derive default API base URLs from the current origin so the app
// works behind a reverse proxy (HTTP or HTTPS) without hard-coded hosts.
const defaultIngestionBase =
  process.env.REACT_APP_API_URL ||
  `${window.location.origin.replace(/\/$/, '')}/api/ingestion`;

const defaultAnalyticsBase =
  process.env.REACT_APP_ANALYTICS_URL ||
  `${window.location.origin.replace(/\/$/, '')}/api/analytics`;

/**
 * Main Axios instance for API requests (Ingestion Service)
 */
const axiosInstance = createAxiosInstance(defaultIngestionBase);

/**
 * Analytics Service Axios instance (for Trino queries)
 */
export const analyticsClient = createAxiosInstance(defaultAnalyticsBase);

/**
 * Error response from API
 */
export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

/**
 * API service for making HTTP requests
 */
export const api = {
  /**
   * Make a GET request
   */
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await axiosInstance.get(url, config);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<ApiError>);
      throw error;
    }
  },
  
  /**
   * Make a POST request
   */
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<ApiError>);
      throw error;
    }
  },
  
  /**
   * Make a PUT request
   */
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<ApiError>);
      throw error;
    }
  },
  
  /**
   * Make a DELETE request
   */
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError<ApiError>);
      throw error;
    }
  },
};

/**
 * Handle API errors
 */
const handleApiError = (error: AxiosError<ApiError>) => {
  if (!error.response) {
    console.error('Network error:', error.message);
    return;
  }
  
  const { status, data } = error.response;
  
  switch (status) {
    case 401:
      console.error('Authentication error:', data?.message || 'Unauthorized');
      break;
    case 403:
      console.error('Authorization error:', data?.message || 'Forbidden');
      break;
    case 404:
      console.error('Not found:', data?.message || 'Resource not found');
      break;
    case 422:
      console.error('Validation error:', data?.errors || data?.message || 'Invalid data');
      break;
    default:
      console.error(`Error (${status}):`, data?.message || 'Unknown error');
  }
};
